/**
 * CopaOnboardingModal — Boas-vindas premium ao Álbum BB 2026.
 *
 * Estrutura fiel ao handoff (handoff_onboarding_frame.html):
 *   1. Header com gradiente + hatch + estrela faded + pill "NOVA CAMPANHA · 1S2026"
 *      + título + subtítulo + botão ✕.
 *   2. Parágrafo intro com destaques teal em negrito.
 *   3. Grid 2×2 de passos com círculos numerados coloridos.
 *   4. CTAs em linha: "COMEÇAR MINHA ESCALAÇÃO" (amarelo) + "VER REGRAS DA CAMPANHA".
 */

import React from 'react';
import Modal from 'react-modal';
import { ThemePlayer } from './components/ThemePlayer';
import styles from './CopaOnboardingModal.module.scss';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface CopaOnboardingModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onViewRules?: () => void;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------
export function CopaOnboardingModal({
  isOpen,
  onConfirm,
  onViewRules,
}: CopaOnboardingModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onConfirm}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      overlayClassName={styles.overlay}
      className={styles.modal}
      contentLabel="Boas-vindas ao Álbum BB 2026"
    >
      {/* ── HEADER ── */}
      <div className={styles.header}>
        {/* Diagonal hatch overlay */}
        <div className={styles.headerHatch} aria-hidden="true" />
        {/* Faded star background */}
        <div className={styles.headerStar} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="#fbfd00">
            <path d="M12 2l2.4 6.9H22l-6 4.4 2.3 7-6.3-4.6L5.7 20.3 8 13.3l-6-4.4h7.6z" />
          </svg>
        </div>
        {/* Close button */}
        <button
          className={styles.closeBtn}
          onClick={onConfirm}
          type="button"
          aria-label="Fechar modal de boas-vindas"
        >
          ✕
        </button>

        <div className={styles.headerContent}>
          {/* Pill "NOVA CAMPANHA · 1S2026" */}
          <div className={styles.campaignPill} aria-label="Nova campanha primeiro semestre 2026">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="#fbfd00" aria-hidden="true">
              <path d="M12 2l2.4 6.9H22l-6 4.4 2.3 7-6.3-4.6L5.7 20.3 8 13.3l-6-4.4h7.6z" />
            </svg>
            Nova campanha · 1S2026
          </div>
          <h2 className={styles.title}>
            O Álbum BB 2026 chegou ao Joga Junto BB
          </h2>
          <p className={styles.subtitle}>
            Reconheça colegas, abra envelopes e complete sua escalação!
          </p>

          {/* Player do tema da Copa (handoff "Player Tema Copa") */}
          <div className={styles.headerPlayer}>
            <ThemePlayer />
          </div>
        </div>
      </div>

      {/* ── CORPO ── */}
      <div className={styles.body}>
        {/* Parágrafo introdutório */}
        <p className={styles.introParagraph}>
          Durante a campanha você poderá colecionar cartas inspiradas na Copa. Cada carta
          representa comportamentos valorizados no Joga Junto —{' '}
          <b className={styles.introHighlight}>foco no cliente, colaboração, inovação, protagonismo</b> e{' '}
          <b className={styles.introHighlight}>liderança integradora</b>.
        </p>

        {/* Grid 2×2 de passos */}
        <div className={styles.stepsGrid} role="list" aria-label="Como funciona a campanha">
          <div className={styles.stepCard} role="listitem">
            <div className={`${styles.stepNumber} ${styles.stepNumberYellow}`} aria-hidden="true">1</div>
            <div>
              <div className={styles.stepTitle}>Abra o Pacote do Dia</div>
              <div className={styles.stepDesc}>Você pode abrir 1 envelope por dia durante a campanha.</div>
            </div>
          </div>
          <div className={styles.stepCard} role="listitem">
            <div className={`${styles.stepNumber} ${styles.stepNumberTeal}`} aria-hidden="true">2</div>
            <div>
              <div className={styles.stepTitle}>Reconheça colegas em junho</div>
              <div className={styles.stepDesc}>Cada reconhecimento enviado em junho rende um envelope bônus.</div>
            </div>
          </div>
          <div className={styles.stepCard} role="listitem">
            <div className={`${styles.stepNumber} ${styles.stepNumberPurple}`} aria-hidden="true">3</div>
            <div>
              <div className={styles.stepTitle}>Colecione cartas</div>
              <div className={styles.stepDesc}>Complete as seções Titulares, Craques e Lendárias.</div>
            </div>
          </div>
          <div className={styles.stepCard} role="listitem">
            <div className={`${styles.stepNumber} ${styles.stepNumberYellow}`} aria-hidden="true">4</div>
            <div>
              <div className={styles.stepTitle}>Complete sua escalação</div>
              <div className={styles.stepDesc}>Quanto mais você participa, mais perto fica de completar o álbum.</div>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className={styles.actions}>
          <button
            className={styles.btnPrimary}
            onClick={onConfirm}
            type="button"
            aria-label="Começar minha escalação"
          >
            Começar minha escalação
          </button>

          {onViewRules && (
            <button
              className={styles.btnSecondary}
              onClick={onViewRules}
              type="button"
              aria-label="Ver regras da campanha Álbum BB 2026"
            >
              Ver regras da campanha
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
